var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx } from "react/jsx-runtime";
import * as React from 'react';
import clsx from 'clsx';
import { HawkinsPropsOverrideContext, isAssumedEventHandlerName, isNil, mergeProps, useArrowKeyNavigation, useClasses, useConsoleLog, useControlledStateWarning, useLayoutProps, useProps, useTranslate, } from '@hawkins/helpers';
import { USE_OVERFLOW_MENU_OVERFLOWABLE_ELEM_ATTR, useCombineRefs, useEffectPostInitialRender, useOverflowMenu, } from '../hooks';
import { ToggleButton } from '../toggleButton';
import { TOGGLE_BUTTON_GROUP_MENU_TRIGGER_ATTR } from './toggleButtonGroup.constants';
import { ToggleButtonGroupContext, } from './toggleButtonGroup.context';
import { ToggleButtonGroupStrings } from './toggleButtonGroup.strings';
import { useToggleButtonGroupStyles } from './toggleButtonGroup.styles';
import { ToggleButtonGroupTestIds } from './toggleButtonGroup.testids';
export var TOGGLE_BUTTON_GROUP_OVERFLOWABLE_ELEM_ATTR = USE_OVERFLOW_MENU_OVERFLOWABLE_ELEM_ATTR;
var displayName = 'ToggleButtonGroup';
var overrideProps = {
    ToggleButton: (_a = {}, _a[TOGGLE_BUTTON_GROUP_OVERFLOWABLE_ELEM_ATTR] = true, _a),
};
// The first selected toggle button should be tabbable.
// If no toggle button is selected, then the first toggle button should be tabbable.
function getTabbableValue(children, value) {
    var childElemsWithValue = React.Children.toArray(children)
        .filter(React.isValidElement)
        // Assume that all top-level child elements are either a `ToggleButton`
        // or a custom component that conforms to the `ToggleButtonProps` interface.
        .filter(function (child) { return React.isValidElement(child) && !isNil(child); });
    if (!childElemsWithValue.length)
        return undefined;
    var selectedValues = Array.isArray(value) ? value : value === null ? [] : [value];
    if (selectedValues.length) {
        var firstSelectedChildElem = childElemsWithValue.find(function (child) { return React.isValidElement(child) && selectedValues.includes(child.props.value); });
        if (React.isValidElement(firstSelectedChildElem))
            return firstSelectedChildElem.props.value;
    }
    var firstChildElemWithValue = childElemsWithValue[0];
    if (React.isValidElement(firstChildElemWithValue))
        return firstChildElemWithValue.props.value;
    return undefined;
}
function getValidExclusiveValue(value) {
    var _a;
    if (Array.isArray(value))
        return (_a = value[0]) !== null && _a !== void 0 ? _a : null;
    return value;
}
function getValidNonExclusiveValue(value) {
    if (Array.isArray(value))
        return value;
    if (value === null)
        return [];
    return [value];
}
/**
 * Toggle buttons can be used to switch between multiple states.
 */
export var ToggleButtonGroup = React.forwardRef(function (_a, ref) {
    var childrenProp = _a.children, props = __rest(_a, ["children"]);
    // Props
    var _b = useProps(displayName, props), classesProp = _b.classes, className = _b.className, _c = _b.compact, compact = _c === void 0 ? false : _c, _d = _b.component, Component = _d === void 0 ? 'div' : _d, defaultValueProp = _b.defaultValue, _e = _b.disabled, disabled = _e === void 0 ? false : _e, _f = _b.disableOverflowableElemsWarning, disableOverflowableElemsWarning = _f === void 0 ? false : _f, _g = _b.disableOverflowMenu, disableOverflowMenu = _g === void 0 ? true : _g, _h = _b.exclusive, exclusive = _h === void 0 ? true : _h, layout = _b.layout, maxDisplayElems = _b.maxDisplayElems, onChangeProp = _b.onChange, styleProp = _b.style, translateProp = _b.translate, valueProp = _b.value, restProps = __rest(_b, ["classes", "className", "compact", "component", "defaultValue", "disabled", "disableOverflowableElemsWarning", "disableOverflowMenu", "exclusive", "layout", "maxDisplayElems", "onChange", "style", "translate", "value"]);
    var children = React.useMemo(function () {
        return React.Children.toArray(childrenProp).map(function (child, idx) {
            var _a;
            return React.isValidElement(child)
                ? React.cloneElement(child, {
                    'data-testid': (_a = child.props['data-testid']) !== null && _a !== void 0 ? _a : "".concat(ToggleButtonGroupTestIds.ITEM_NTH).concat(idx),
                })
                : child;
        });
    }, [childrenProp]);
    // For backwards compatibility, accept any value except `undefined`.
    //
    // TODO(API): Make this more strict in a future major version.
    var hasDefaultValue = defaultValueProp !== undefined;
    var isValueControlled = valueProp !== undefined;
    var defaultValue = hasDefaultValue ? defaultValueProp : null;
    var isOverflowMenuEnabled = disableOverflowMenu === false;
    useConsoleLog(hasDefaultValue && exclusive && Array.isArray(defaultValueProp), "`props.defaultValue` for an exclusive `ToggleButtonGroup` must NOT be an array. Received `".concat(typeof defaultValueProp, "`."), { displayName: displayName, docsPath: 'components-toggle-button#exclusivity', level: 'error' });
    useConsoleLog(hasDefaultValue && !exclusive && !Array.isArray(defaultValueProp), "`props.defaultValue` for a non-exclusive `ToggleButtonGroup` must be an array. Received `".concat(typeof defaultValueProp, "`."), { displayName: displayName, docsPath: 'components-toggle-button#exclusivity', level: 'error' });
    useConsoleLog(isValueControlled && exclusive && Array.isArray(valueProp), "`props.value` for an exclusive `ToggleButtonGroup` must NOT be an array. Received `".concat(typeof valueProp, "`."), { displayName: displayName, docsPath: 'components-toggle-button#exclusivity', level: 'error' });
    useConsoleLog(isValueControlled && !exclusive && !Array.isArray(valueProp), "`props.value` for a non-exclusive `ToggleButtonGroup` must be an array. Received `".concat(typeof valueProp, "`."), { displayName: displayName, docsPath: 'components-toggle-button#exclusivity', level: 'error' });
    useControlledStateWarning(valueProp, { displayName: displayName });
    // State
    var _j = __read(React.useState(exclusive ? getValidExclusiveValue(defaultValue) : getValidNonExclusiveValue(defaultValue)), 2), internalValue = _j[0], setInternalValue = _j[1];
    // Ensure that `internalValue` remains valid even if the exclusivity setting changes.
    useEffectPostInitialRender(function () {
        if (isValueControlled)
            return;
        setInternalValue(function (prev) { return (exclusive ? getValidExclusiveValue(prev) : getValidNonExclusiveValue(prev)); });
    }, [exclusive, isValueControlled]);
    var value = React.useMemo(function () {
        var rawValue = isValueControlled ? valueProp : internalValue;
        return exclusive ? getValidExclusiveValue(rawValue) : getValidNonExclusiveValue(rawValue);
    }, [exclusive, internalValue, isValueControlled, valueProp]);
    // Strings
    var translate = useTranslate(translateProp, ToggleButtonGroupStrings);
    // Context
    var onChange = React.useCallback(function (ev, buttonValue) {
        var rawNewValue = buttonValue;
        // We currently don't support deselecting all toggle buttons,
        // so `rawNewValue` will always be set to `buttonValue` in an exclusive toggle button group.
        if (!exclusive) {
            if (Array.isArray(value)) {
                rawNewValue = value.includes(buttonValue)
                    ? value.filter(function (v) { return v !== buttonValue; })
                    : __spreadArray(__spreadArray([], __read(value), false), [buttonValue], false);
            }
            else if (value === null) {
                rawNewValue = [buttonValue];
            }
            else {
                rawNewValue = value === buttonValue ? [] : [value, buttonValue];
            }
        }
        var newValue = exclusive ? getValidExclusiveValue(rawNewValue) : getValidNonExclusiveValue(rawNewValue);
        if (!isValueControlled)
            setInternalValue(newValue);
        // We currently don't support deselecting all toggle buttons, which means `newValue` here will never be `null`.
        onChangeProp === null || onChangeProp === void 0 ? void 0 : onChangeProp(ev, newValue);
    }, [exclusive, isValueControlled, onChangeProp, value]);
    var groupContext = React.useMemo(function () { return ({ compact: compact, disabled: disabled, exclusive: exclusive, onChange: onChange, tabbableValue: getTabbableValue(children, value), value: value }); }, [children, compact, disabled, exclusive, onChange, value]);
    // Overflow menu logic
    var getMenuItemProps = React.useCallback(function (item, _a) {
        var idx = _a.idx;
        var isSelected = Array.isArray(value) ? value.includes(item.props.value) : value === item.props.value;
        return __assign(__assign({}, Object.entries(item.props).reduce(function (result, _a) {
            var _b = __read(_a, 2), propName = _b[0], propValue = _b[1];
            // `MenuItem` and `ToggleButton` don't support all the same props, but at least
            // try to pass through event handlers they might have in common like `onFocus`.
            if (isAssumedEventHandlerName(propName) &&
                // Handle `onChange` and `onClick` separately.
                propName !== 'onChange' &&
                propName !== 'onClick') {
                result[propName] = propValue;
            }
            return result;
        }, {})), { children: item.props.children, disabled: disabled || item.props.disabled, icon: item.props.icon, key: "".concat(item.props.children, "_").concat(idx), onClick: function (ev) {
                var _a, _b;
                (_b = (_a = item.props).onChange) === null || _b === void 0 ? void 0 : _b.call(_a, ev, item.props.value, !isSelected);
                onChange(ev, item.props.value);
            }, selected: isSelected, selectType: exclusive ? 'single' : 'multi' });
    }, [disabled, exclusive, onChange, value]);
    var getMenuProps = React.useCallback(function (_a) {
        var _b;
        var isDone = _a.isDone, menuTriggerAttr = _a.menuTriggerAttr, overflowElemsRef = _a.overflowElemsRef;
        return ({
            trigger: (_jsx(ToggleButton, __assign({}, (_b = {}, _b[menuTriggerAttr] = true, _b), { value: isDone &&
                    value !== null &&
                    overflowElemsRef.current.some(function (elem) {
                        return Array.isArray(value) ? value.includes(elem.props.value) : value === elem.props.value;
                    })
                    ? Array.isArray(value)
                        ? value[0]
                        : value
                    : 'hawkins-overflow-menu-trigger' }, { children: translate('toggleButtonGroup.overflowMenu.label') }))),
        });
    }, [translate, value]);
    var _k = useOverflowMenu({
        children: children,
        disabled: !isOverflowMenuEnabled,
        disableOverflowableElemsWarning: disableOverflowableElemsWarning,
        displayName: displayName,
        getMenuItemProps: getMenuItemProps,
        getMenuProps: getMenuProps,
        maxDisplayElems: maxDisplayElems,
        menuTriggerAttr: TOGGLE_BUTTON_GROUP_MENU_TRIGGER_ATTR,
        overflowableElemAttr: TOGGLE_BUTTON_GROUP_OVERFLOWABLE_ELEM_ATTR,
    }), containerRef = _k.containerRef, contentNode = _k.contentNode;
    var _l = useArrowKeyNavigation({
        supportLeftRightKeys: true,
        // A radiogroup should be navigable using the up/down arrow keys.
        supportUpDownKeys: exclusive,
    }), containerCallbacks = _l.containerCallbacks, navigableContainerRef = _l.containerRef;
    var combinedRef = useCombineRefs([containerRef, navigableContainerRef, ref]);
    // Styles
    var baseClasses = useToggleButtonGroupStyles();
    var classes = useClasses(displayName, baseClasses, classesProp);
    var style = useLayoutProps({ layout: layout, style: styleProp });
    var rootClassName = clsx(classes.HAWKINS_BASE, className, classes.root);
    return (_jsx(Component, __assign({ className: rootClassName, "data-testid": ToggleButtonGroupTestIds.ROOT, ref: combinedRef, 
        // An exclusive toggle button group should be a radiogroup (see the "Radio Group (Text Alignment)"
        // example on https://www.w3.org/TR/wai-aria-practices-1.1/examples/toolbar/toolbar.html).
        role: exclusive ? 'radiogroup' : undefined, style: style }, mergeProps(restProps, containerCallbacks), { children: _jsx(ToggleButtonGroupContext.Provider, __assign({ value: groupContext }, { children: _jsx(HawkinsPropsOverrideContext.Provider, __assign({ value: overrideProps }, { children: contentNode })) })) })));
});
ToggleButtonGroup.displayName = displayName;
